/**
 * PublicActionForm — draws the form an action button opens, and submits it.
 *
 * The fields come from Form Builder (`FormDefinition.fields_schema`), sent down
 * with the page. Nothing about the target tables, the operations or the
 * server-derived columns reaches the browser: the submit carries a button id
 * plus answers, and the server decides everything structural from its own
 * configuration.
 *
 * Hidden template values (`data-arcee-payload` inputs written into the layout)
 * are gathered from the anchor and sent alongside the answers. They carry the
 * same trust level as a typed answer — the server's allowlist, not their
 * origin, is what decides whether either may fill a given column.
 */

import React, { useState } from 'react';

function fieldInput(field, value, onChange) {
  const common = {
    id: `arcee-field-${field.id}`,
    className: 'form-control',
    value: value ?? '',
    required: Boolean(field.required),
    onChange: e => onChange(field.id, e.target.value),
  };

  if (field.type === 'textarea') return <textarea rows={4} {...common} />;
  if (field.type === 'select') {
    return (
      <select {...common} className="form-select">
        <option value="">— Select —</option>
        {(field.options || []).map(opt => {
          const val = typeof opt === 'string' ? opt : opt.value;
          const label = typeof opt === 'string' ? opt : (opt.label || opt.value);
          return <option key={val} value={val}>{label}</option>;
        })}
      </select>
    );
  }
  const type = field.type === 'email' ? 'email'
    : field.type === 'number' ? 'number'
    : field.type === 'date' ? 'date'
    : field.type === 'tel' ? 'tel'
    : 'text';
  return <input type={type} {...common} />;
}

export default function PublicActionForm({ button, form, hiddenValues, onSubmit, state }) {
  const [answers, setAnswers] = useState({});

  if (!form) return null;

  const busy = state?.status === 'busy';
  const done = state?.status === 'done';

  function setAnswer(id, value) {
    setAnswers(prev => ({ ...prev, [id]: value }));
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (busy) return;
    onSubmit({ button, answers, payload: hiddenValues || {} });
  }

  if (done) {
    // A reveal means the page is showing the pass in a modal. Repeating the
    // message underneath it says the same thing twice and leaves a stale
    // confirmation on the page once the modal is dismissed.
    if (state.reveal) return null;

    return (
      <div className="alert alert-success" role="status">
        {state.message || form.success_message}
      </div>
    );
  }

  return (
    <form className="arcee-action-form" onSubmit={handleSubmit} noValidate={false}>
      {form.title && <h3 className="h5">{form.title}</h3>}

      {state?.status === 'error' && (
        <div className="alert alert-danger" role="alert">
          {state.message}
          {Array.isArray(state.detail) && (
            <ul className="mb-0 mt-1">
              {state.detail.map((line, i) => <li key={i}>{line}</li>)}
            </ul>
          )}
        </div>
      )}

      {(form.fields || []).map(field => (
        <div className="mb-3" key={field.id}>
          <label className="form-label" htmlFor={`arcee-field-${field.id}`}>
            {field.label || field.id}
            {field.required && <span aria-hidden="true"> *</span>}
          </label>
          {fieldInput(field, answers[field.id], setAnswer)}
          {field.help_text && <div className="form-text">{field.help_text}</div>}
        </div>
      ))}

      <button type="submit" className="btn btn-primary" disabled={busy}>
        {busy ? 'Working…' : (form.button_text || button?.label || 'Submit')}
      </button>
    </form>
  );
}
